/**
 * DbTag - 标签组件
 *
 * 特性：
 * - 支持多种预设类型（hot、new、促销、七天无理由等）
 * - 支持实体（solid）和虚体（outline）风格
 * - 支持自定义颜色、尺寸、形状
 * - 支持左右图标
 * - 支持关闭按钮
 * - 支持点击交互
 */

import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, ViewStyle, TextStyle } from 'react-native';
import {
    DbTagProps,
    DbTagGroupProps,
    TagVariant,
    TagPreset,
    TagSize,
    TagShape,
} from './DbTag.types';
import {
    createStyles,
    PRESET_COLORS,
    SIZE_CONFIG,
    getShapeRadius,
} from './DbTag.styles';
import DbIcon from '../DbIcon';

// ==================== DbTag 组件 ====================

export const DbTag: React.FC<DbTagProps> & {
    Group: typeof DbTagGroup;
} = ({
    text,
    children,
    preset = 'default',
    variant = 'solid',
    size = 'small',
    shape = 'rounded',
    color,
    backgroundColor,
    borderColor,
    textColor,
    icon,
    rightIcon,
    closable = false,
    onClose,
    onPress,
    disabled = false,
    pressable,
    style,
    textStyle,
    borderWidth = 1,
    borderRadius,
}) => {
    const styles = useMemo(() => createStyles(), []);
    const sizeConfig = SIZE_CONFIG[size];
    const presetColor = PRESET_COLORS[preset];

    // 计算实际颜色
    const computedColors = useMemo(() => {
        const primaryColor = color || presetColor.primary;
        
        let bgColor: string;
        let bdColor: string;
        let txColor: string;

        switch (variant) {
            case 'solid':
                bgColor = backgroundColor || primaryColor;
                bdColor = borderColor || primaryColor;
                txColor = textColor || '#FFFFFF';
                break;
            case 'outline':
                bgColor = backgroundColor || 'transparent';
                bdColor = borderColor || primaryColor;
                txColor = textColor || primaryColor;
                break;
            case 'light':
                bgColor = backgroundColor || presetColor.light;
                bdColor = borderColor || 'transparent';
                txColor = textColor || presetColor.text;
                break;
            case 'text':
                bgColor = backgroundColor || 'transparent';
                bdColor = borderColor || 'transparent';
                txColor = textColor || color || presetColor.text;
                break;
            default:
                bgColor = backgroundColor || primaryColor;
                bdColor = borderColor || primaryColor;
                txColor = textColor || '#FFFFFF';
        }

        return {
            backgroundColor: bgColor,
            borderColor: bdColor,
            textColor: txColor,
        };
    }, [variant, color, backgroundColor, borderColor, textColor, presetColor]);

    // 计算圆角
    const computedBorderRadius = useMemo(() => {
        if (borderRadius !== undefined) return borderRadius;
        return getShapeRadius(shape, sizeConfig.minHeight);
    }, [shape, sizeConfig.minHeight, borderRadius]);

    // 容器样式
    const containerStyle: ViewStyle = {
        backgroundColor: computedColors.backgroundColor,
        borderColor: computedColors.borderColor,
        borderWidth: variant === 'light' || variant === 'text' ? 0 : borderWidth,
        borderRadius: computedBorderRadius,
        paddingHorizontal: variant === 'text' ? 0 : sizeConfig.paddingH,
        paddingVertical: variant === 'text' ? 0 : sizeConfig.paddingV,
        minHeight: variant === 'text' ? undefined : sizeConfig.minHeight,
    };

    // 文本样式
    const computedTextStyle: TextStyle = {
        fontSize: sizeConfig.fontSize,
        color: computedColors.textColor,
    };

    // 内容
    const content = children || text;

    // 渲染内容
    const renderContent = () => (
        <>
            {icon && (
                <View style={styles.iconLeft}>
                    {icon}
                </View>
            )}
            {typeof content === 'string' ? (
                <Text style={[styles.text, computedTextStyle, textStyle]} numberOfLines={1}>
                    {content}
                </Text>
            ) : (
                content
            )}
            {rightIcon && (
                <View style={styles.iconRight}>
                    {rightIcon}
                </View>
            )}
            {closable && (
                <TouchableOpacity
                    style={styles.closeButton}
                    onPress={onClose}
                    hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
                >
                    <DbIcon
                        name="close"
                        family="Ionicons"
                        size={sizeConfig.iconSize}
                        color={computedColors.textColor}
                        style={styles.closeIcon}
                    />
                </TouchableOpacity>
            )}
        </>
    );

    // 是否可点击
    const isPressable = pressable !== undefined ? pressable : !!onPress;

    if (isPressable && !disabled) {
        return (
            <TouchableOpacity
                style={[
                    styles.container,
                    containerStyle,
                    disabled && styles.disabled,
                    style,
                ]}
                onPress={onPress}
                activeOpacity={0.7}
                disabled={disabled}
            >
                {renderContent()}
            </TouchableOpacity>
        );
    }

    return (
        <View
            style={[
                styles.container,
                containerStyle,
                disabled && styles.disabled,
                style,
            ]}
        >
            {renderContent()}
        </View>
    );
};

// ==================== DbTagGroup 组件 ====================

export const DbTagGroup: React.FC<DbTagGroupProps> = ({
    children,
    gap = 8,
    wrap = true,
    align = 'left',
    style,
}) => {
    const styles = useMemo(() => createStyles(), []);

    const groupStyle: ViewStyle = {
        gap,
    };

    return (
        <View
            style={[
                styles.tagGroup,
                !wrap && styles.tagGroupNoWrap,
                align === 'center' && styles.tagGroupAlignCenter,
                align === 'right' && styles.tagGroupAlignRight,
                groupStyle,
                style,
            ]}
        >
            {children}
        </View>
    );
};

// 挂载子组件
DbTag.Group = DbTagGroup;
DbTag.displayName = 'DbTag';

export default DbTag;
export type {
    DbTagProps,
    DbTagGroupProps,
    TagVariant,
    TagPreset,
    TagSize,
    TagShape,
} from './DbTag.types';
